import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Stack,
  Alert,
  Divider,
  Grid,
  CircularProgress,
  useTheme,
  alpha,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Psychology as AIIcon,
  TrendingUp,
  Warning,
  CheckCircle,
  ExpandMore,
  Help as QuestionIcon,
  Star
} from '@mui/icons-material';
import { AIMatchingResult, checkAIEnabled } from '../services/aiResumeParserService';

interface AIResumeAnalysisProps {
  analysisResult?: AIMatchingResult;
  onAnalyze?: () => Promise<void>;
  loading?: boolean;
}

export const AIResumeAnalysis: React.FC<AIResumeAnalysisProps> = ({
  analysisResult,
  onAnalyze,
  loading = false
}) => {
  const theme = useTheme();
  const [aiEnabled, setAiEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    checkAIEnabled().then(setAiEnabled);
  }, []);

  if (aiEnabled === null) {
    return (
      <Paper elevation={0} sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress size={24} />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Checking AI availability...
        </Typography>
      </Paper>
    );
  }

  if (!aiEnabled) {
    return (
      <Alert severity="info" icon={<AIIcon />}>
        <Typography variant="body2" fontWeight={600}>
          AI Analysis Available
        </Typography>
        <Typography variant="caption">
          Configure OpenAI API key to enable intelligent resume analysis
        </Typography>
      </Alert>
    );
  }

  if (!analysisResult && !loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <AIIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              AI-Powered Analysis
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Get intelligent insights, match scoring, and interview questions
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AIIcon />}
            onClick={onAnalyze}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
            }}
          >
            Analyze with AI
          </Button>
        </Stack>
      </Paper>
    );
  }

  if (loading) {
    return (
      <Paper elevation={0} sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress size={40} sx={{ mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          AI is analyzing the resume...
        </Typography>
        <Typography variant="caption" color="text.secondary">
          This may take 10-30 seconds
        </Typography>
      </Paper>
    );
  }

  if (!analysisResult) return null;

  const getScoreColor = (score: number) => {
    if (score >= 80) return theme.palette.success.main;
    if (score >= 60) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const getConfidenceBadge = (confidence: string) => {
    const colors = {
      high: 'success',
      medium: 'warning',
      low: 'error'
    };
    return colors[confidence as keyof typeof colors] || 'default';
  };

  return (
    <Box>
      {/* Overall Score Card */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 2,
          border: `2px solid ${alpha(getScoreColor(analysisResult.overallScore), 0.3)}`,
          background: `linear-gradient(135deg, ${alpha(getScoreColor(analysisResult.overallScore), 0.08)} 0%, transparent 100%)`
        }}
      >
        <Stack direction="row" alignItems="center" spacing={3}>
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `conic-gradient(${getScoreColor(analysisResult.overallScore)} ${analysisResult.overallScore * 3.6}deg, ${alpha(getScoreColor(analysisResult.overallScore), 0.2)} 0deg)`,
              position: 'relative'
            }}
          >
            <Box
              sx={{
                width: 85,
                height: 85,
                borderRadius: '50%',
                bgcolor: 'background.paper',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Typography variant="h3" fontWeight={800} color={getScoreColor(analysisResult.overallScore)}>
                {analysisResult.overallScore}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <AIIcon sx={{ color: theme.palette.primary.main }} />
              <Typography variant="h5" fontWeight={700}>
                AI Match Analysis
              </Typography>
              <Chip
                label={`${analysisResult.confidence} confidence`}
                size="small"
                color={getConfidenceBadge(analysisResult.confidence) as any}
                sx={{ fontWeight: 600 }}
              />
            </Stack>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {analysisResult.summary}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Skills Match
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {analysisResult.skillsMatch.score}%
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Experience
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {analysisResult.experienceMatch.score}%
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Education
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {analysisResult.educationMatch.score}%
                  </Typography>
                </Box>
              </Grid>
              {analysisResult.cultureFit && (
                <Grid item xs={3}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Culture Fit
                    </Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {analysisResult.cultureFit.score}%
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </Stack>
      </Paper>

      {/* Expandable Details */}
      <Paper elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
        {/* Strengths */}
        <Accordion disableGutters elevation={0}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CheckCircle sx={{ color: 'success.main' }} />
              <Typography variant="subtitle1" fontWeight={600}>
                Strengths ({analysisResult.strengths.length})
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              {analysisResult.strengths.map((strength, index) => (
                <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
                  <Star sx={{ fontSize: 16, color: 'success.main', mt: 0.3 }} />
                  <Typography variant="body2">{strength}</Typography>
                </Stack>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Divider />

        {/* Concerns */}
        {analysisResult.concerns.length > 0 && (
          <>
            <Accordion disableGutters elevation={0}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Warning sx={{ color: 'warning.main' }} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Concerns ({analysisResult.concerns.length})
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1}>
                  {analysisResult.concerns.map((concern, index) => (
                    <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
                      <Warning sx={{ fontSize: 16, color: 'warning.main', mt: 0.3 }} />
                      <Typography variant="body2">{concern}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
            <Divider />
          </>
        )}

        {/* Skills Breakdown */}
        <Accordion disableGutters elevation={0}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <TrendingUp sx={{ color: 'primary.main' }} />
              <Typography variant="subtitle1" fontWeight={600}>
                Skills Analysis
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Matched Skills
                </Typography>
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  {analysisResult.skillsMatch.matchedSkills.map((skill, index) => (
                    <Chip
                      key={index}
                      label={skill}
                      size="small"
                      sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: 'success.dark' }}
                    />
                  ))}
                </Stack>
              </Box>

              {analysisResult.skillsMatch.missingSkills.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Missing Skills
                  </Typography>
                  <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {analysisResult.skillsMatch.missingSkills.map((skill, index) => (
                      <Chip
                        key={index}
                        label={skill}
                        size="small"
                        sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), color: 'error.dark' }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {analysisResult.skillsMatch.additionalSkills.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Additional Skills (Bonus)
                  </Typography>
                  <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {analysisResult.skillsMatch.additionalSkills.map((skill, index) => (
                      <Chip
                        key={index}
                        label={skill}
                        size="small"
                        sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: 'info.dark' }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Divider />

        {/* Interview Questions */}
        {analysisResult.interviewQuestions && analysisResult.interviewQuestions.length > 0 && (
          <Accordion disableGutters elevation={0}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <QuestionIcon sx={{ color: 'secondary.main' }} />
                <Typography variant="subtitle1" fontWeight={600}>
                  AI-Generated Interview Questions ({analysisResult.interviewQuestions.length})
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                {analysisResult.interviewQuestions.map((question, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: alpha(theme.palette.secondary.main, 0.05),
                      border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`
                    }}
                  >
                    <Typography variant="body2">
                      <strong>{index + 1}.</strong> {question}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Recommendations */}
        {analysisResult.recommendations.length > 0 && (
          <>
            <Divider />
            <Accordion disableGutters elevation={0}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Recommendations
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1}>
                  {analysisResult.recommendations.map((rec, index) => (
                    <Typography key={index} variant="body2">
                      • {rec}
                    </Typography>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </Paper>
    </Box>
  );
};
